import React, { useState, useRef, useEffect } from 'react'
import Input from '../../../components/form/input/InputField'
import { Circle, Frown, X } from 'lucide-react'
import Label from '../../../components/form/Label'
import { useBusiness } from '../../../context/BusinessContext'
import { FilterType } from '../../../context/CommonContext'

const Filter = ({
    title,
    type,
    selecteds,
    options,
    disabledLabel = false
}: {
    title: string
    type: string
    selecteds: FilterType[]
    options: FilterType[]
    disabledLabel?: boolean
}) => {
    const { filters, setFilters } = useBusiness()
    const [search, setSearch] = useState('')
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const handleSelect = (option: FilterType) => {
        if (!selecteds.some(item => item.id === option.id)) {
            setFilters({
                ...filters,
                [type]: [...selecteds, option]
            })
            setSearch('')
            setTimeout(() => inputRef.current?.focus(), 0)
        }
    }

    const handleRemove = (id: number) => {
        setFilters({
            ...filters,
            [type]: selecteds.filter(item => item.id !== id)
        })
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && filteredOptions.length > 0) {
            handleSelect(filteredOptions[0])
        }
    }

    const filteredOptions = options.filter(
        option =>
            !selecteds.some(item => item.id === option.id) && option.label.toLowerCase().includes(search.toLowerCase())
    )

    return (
        <div className='w-full relative' ref={dropdownRef}>
            {!disabledLabel && <Label>{title}</Label>}
            <div
                className={`border dark:border-gray-700 rounded-lg px-3 mt-0 bg-white dark:bg-gray-800 dark:text-gray-100 cursor-pointer flex gap-1 text-sm items-center overflow-hidden ${
                    selecteds.length === 0 ? 'py-3' : 'py-1'
                }`}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
                {selecteds.length > 0 ? (
                    selecteds.map(option => (
                        <div
                            key={option.id}
                            className={`border-2 dark:border-gray-700  px-2 py-1.5 rounded-lg flex flex-row items-center gap-1 text-xs`}
                            style={{
                                borderColor: option?.color || ''
                            }}
                        >
                            <p className='truncate'>{option.label}</p>
                            <button
                                onClick={e => {
                                    e.stopPropagation()
                                    handleRemove(option.id)
                                }}
                                className='font-bold text-xs'
                            >
                                <X size={12} />
                            </button>
                        </div>
                    ))
                ) : (
                    <p className='text-gray-400'>{title}</p>
                )}
            </div>
            {isDropdownOpen && (
                <div className='absolute left-0 top-full w-full bg-white dark:bg-gray-800 dark:text-gray-100 border dark:border-gray-700 rounded-lg shadow-md max-h-80 overflow-auto z-10 text-sm'>
                    <div className='px-4 py-2'>
                        <Input
                            ref={inputRef}
                            placeholder='Buscar...'
                            value={search}
                            size='sm'
                            onChange={e => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                            autoFocus
                        />
                    </div>
                    <div className='max-h-60 overflow-auto'>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(option => (
                                <div
                                    key={option.id}
                                    className='px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center'
                                    onClick={() => handleSelect(option)}
                                >
                                    {option?.color && (
                                        <Circle
                                            className='mr-2 flex-shrink-0'
                                            size={12}
                                            fill={option.color}
                                            color={option.color}
                                        />
                                    )}
                                    {option.label}
                                </div>
                            ))
                        ) : (
                            <div className='px-4 py-4 text-gray-500 dark:text-gray-400 text-sm flex items-center gap-1'>
                                <Frown size={16} />
                                <p>Nenhuma opção encontrada!</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default Filter
